// src/hooks/useHostInvitations.ts - Updated for self-contained game_hosts table
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {supabase} from '@/services/supabase';
import {GameHost, CreateGameHostData, InvitationResult, UpdateGameHostData} from '@/types';
import toast from 'react-hot-toast';

// =====================================================
// MAIN ORCHESTRATION FUNCTION
// =====================================================

export const useCreateGameHostWithInvitation = () => {
    const queryClient = useQueryClient();

    return useMutation<InvitationResult, Error, CreateGameHostData>({
        mutationFn: async (hostData: CreateGameHostData): Promise<InvitationResult> => {
            console.log('➕ Creating host user via Edge Function:', hostData.email);

            const {data: {session}} = await supabase.auth.getSession();
            if (!session?.access_token) {
                throw new Error('No authentication token available');
            }

            const invitationUrl = `${window.location.origin}/accept-invitation`;

            // Call your existing Edge Function (now with secure tokens)
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-host-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    email: hostData.email,
                    first_name: hostData.first_name,
                    last_name: hostData.last_name,
                    phone: hostData.phone,
                    account_id: hostData.account_id,
                    account_type: hostData.account_type,
                    game_access: hostData.game_access || [],
                    send_invitation: hostData.send_invitation,
                    redirectTo: invitationUrl
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Failed to create host: ${response.statusText}`);
            }

            const result = await response.json();
            console.log(`✅ Host created successfully: ${hostData.email}`);

            return {
                host: result.data,
                invitation: {
                    hostId: result.data.id,
                    email: result.data.email,
                    invitationSent: result.emailSent
                }
            };
        },
        onSuccess: (data) => {
            const message = data.invitation.invitationSent
                ? `✅ Host created and secure invitation sent to ${data.host.email}`
                : `✅ Host created successfully: ${data.host.email}`;

            toast.success(message);

            queryClient.invalidateQueries({queryKey: ['game-hosts']});
            queryClient.invalidateQueries({queryKey: ['pending-invitations']});
        },
        onError: (error) => {
            console.error('❌ Create host error:', error);
            toast.error(error.message || 'Failed to create host');
        },
    });
};

// Update game host mutation
export const useUpdateGameHost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({id, data}: { id: string; data: UpdateGameHostData }): Promise<GameHost> => {
            const {data: result, error} = await supabase
                .from('game_hosts')
                .update(data)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return result;
        },
        onSuccess: () => {
            // Invalidate multiple query keys since this can be used from different pages
            queryClient.invalidateQueries({queryKey: ['game-hosts']});
            queryClient.invalidateQueries({queryKey: ['account-detail']});
            queryClient.invalidateQueries({queryKey: ['host-permissions']});
            toast.success('Host updated successfully');
        },
        onError: (error: any) => {
            toast.error(`Failed to update host: ${error.message}`);
        },
    });
};

// Soft delete game host (updated for self-contained game_hosts table)
export const useSoftDeleteGameHost = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async (hostId: string) => {
            // Get the host record first to check invitation status
            const {data: host, error: fetchError} = await supabase
                .from('game_hosts')
                .select('email, invitation_status')
                .eq('id', hostId)
                .single();

            if (fetchError) {
                throw new Error(`Failed to fetch host data: ${fetchError.message}`);
            }

            if (!host) {
                throw new Error('Host not found');
            }

            // Step 1: Soft delete the game host record
            const {error: softDeleteError} = await supabase
                .from('game_hosts')
                .update({
                    deleted_at: new Date().toISOString(),
                    status: 'inactive',
                    updated_at: new Date().toISOString()
                })
                .eq('id', hostId);

            if (softDeleteError) {
                throw new Error(`Failed to soft delete game host: ${softDeleteError.message}`);
            }

            // Step 2: Always try to delete the auth user (if exists)
            // This handles both accepted and sent invitations
            console.log(`🗑️ Attempting to delete auth user for host: ${host.email}`);

            try {
                // Get current session for authorization
                const {data: {session}} = await supabase.auth.getSession();
                if (!session?.access_token) {
                    throw new Error('No authentication token available');
                }

                // Call edge function to delete the auth user by email
                const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-auth-user`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                        email: host.email,
                        hostId: hostId // Pass host ID for logging
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    console.warn('⚠️ Warning: Could not delete auth user:', errorData.error);
                    // Don't throw here as the main soft delete succeeded
                } else {
                    const result = await response.json();
                    if (result.data?.deleted) {
                        console.log(`✅ Successfully deleted auth user for: ${host.email}`);
                    } else {
                        console.log(`ℹ️ No auth user found to delete for: ${host.email}`);
                    }
                }
            } catch (authDeleteError) {
                console.warn('⚠️ Warning: Error deleting auth user:', authDeleteError);
                // Don't throw here as the main soft delete succeeded
            }
        },
        onSuccess: () => {
            toast.success('Game host removed successfully');
            queryClient.invalidateQueries({queryKey: ['game-hosts']});
            queryClient.invalidateQueries({queryKey: ['account-detail']});
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to remove game host');
        },
    });
};
